import { UilTimes } from '@iconscout/react-unicons';
import React, { useState, useMemo, ReactNode, createContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleInfo,
  faCircleExclamation,
  faCircleCheck
} from '@fortawesome/free-solid-svg-icons';
import { Toast } from 'react-bootstrap';

export const ToastContext = createContext<{
  open: (content: string, type?: 'success' | 'error' | 'info') => void;
}>({
  open: () => {}
});

type ToastItem = {
  content: string;
  style: ToastStyle;
};
type ToastStyle = {
  icon: ReactNode;
  background: string;
  text: string;
};

type ToastProviderProps = {
  children: ReactNode;
};

const typeBasedStyle: Record<string, ToastStyle> = {
  info: {
    icon: (
      <div className="shadow rounded-circle p-2 d-flex justify-content-center bg-primary">
        <FontAwesomeIcon icon={faCircleInfo} size="lg" color="white" />
      </div>
    ),
    background: 'alert-subtle-info',
    text: 'Info'
  },
  success: {
    icon: (
      <div className="shadow rounded-circle p-2 d-flex justify-content-center bg-success">
        <FontAwesomeIcon icon={faCircleCheck} size="lg" color="white" />
      </div>
    ),
    background: 'alert-subtle-success',
    text: 'Success'
  },
  error: {
    icon: (
      <div className="shadow rounded-circle p-2 d-flex justify-content-center bg-danger">
        <FontAwesomeIcon icon={faCircleExclamation} size="lg" color="white" />
      </div>
    ),
    background: 'alert-subtle-danger',
    text: 'Error'
  }
};

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const open = (content: string, type: 'success' | 'error' | 'info' = 'info') =>
    setToasts(currentToasts => [
      ...currentToasts,
      { content, style: typeBasedStyle[type] }
    ]);

  const close = (idx: number) =>
    setToasts(currentToasts =>
      currentToasts.filter((toast, index) => idx !== index)
    );

  const contextValue = useMemo(() => ({ open }), []);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}

      <div
        className="toasts-wrapper position-fixed top-0 end-0 p-3"
        style={{ zIndex: 10000 }}
      >
        {toasts.map((toast, idx) => (
          <Toast
            key={idx}
            delay={3000}
            onClose={() => close(idx)}
            autohide
            className={`fade alert show ${toast.style.background} p-2`}
            animation
          >
            <Toast.Body className="d-flex align-items-center w-100 p-1">
              <div className="me-3">{toast.style.icon}</div>
              <div className="d-flex flex-column flex-grow-1">
                <div className="text-body fs-8 fw-bold">{toast.style.text}</div>
                <div className="text-body text-body-tertiary fs-9">
                  {toast.content}
                </div>
              </div>
              <div className="d-flex justify-content-end ms-2">
                <UilTimes
                  className="cursor-pointer"
                  onClick={() => close(idx)}
                />
              </div>
            </Toast.Body>
          </Toast>
        ))}
      </div>
    </ToastContext.Provider>
  );
};
